import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Guard } from '../App';
import type { CommandTurn } from './command-chat';
import {
  archiveThroughCommandSeq,
  commandTurnsInArchiveRange,
  continuedFromPreviousChat,
  latestArchive,
  listRecentMainCommandChats,
  loadMainCommandChatArchives,
  loadMainCommandConversationState,
  previousArchive,
  sortArchivesNewestFirst,
  timelineInArchiveRange,
  withRolloverChatArchive,
  type MainCommandChatArchive,
  type MainCommandConversationState,
  type RecentMainCommandChat,
} from './main-command-chat-archive';
import { useContinueMainCommandChat, useStartMainCommandChat } from './mutations';
import { startMainCommandFreshContext, type FreshContextResult } from './fresh-context';
import {
  isDiscussionMode,
  type CommandDiscussionMode,
  type SharedTimelineItem,
} from './shared-command-timeline';
import {
  buildConversationDigest,
  redactDigestSecrets,
  titleFromFirstOwnerGoal,
  type DigestTurn,
} from '@brunel/shared';

export interface MainCommandConversationController {
  readonly archivesLoaded: boolean;
  readonly orderedArchives: readonly MainCommandChatArchive[];
  readonly conversation: MainCommandConversationState | null;
  readonly recentChats: readonly RecentMainCommandChat[];
  readonly viewingArchiveId: string | null;
  readonly viewingArchive: MainCommandChatArchive | null;
  readonly visibleTimeline: readonly SharedTimelineItem[];
  readonly visibleTurns: readonly CommandTurn[];
  readonly chatBusy: boolean;
  readonly chatError: string | null;
  readonly orgId: string | null;
  readonly selectConversation: (archiveId: string | null) => void;
  readonly startNewChat: () => void;
  readonly continueFromArchive: (archiveId: string) => void;
  /** #688 Start Fresh Context: a new Overseer context epoch; this chat and its tasks stay. */
  readonly startFreshContext?: () => void;
  readonly freshContextResult?: FreshContextResult | null;
  /** Set on the current page when an acknowledged rollover opened it. */
  readonly continuedFromPreviousChat?: string | null;
}

export function useMainCommandConversationController(input: {
  readonly turns: readonly CommandTurn[];
  readonly client: SupabaseClient;
  readonly guard: Guard;
  readonly conversationKey?: string;
  readonly timeline?: readonly SharedTimelineItem[];
  readonly discussion?: CommandDiscussionMode;
  readonly onLeaveDiscussion?: () => void;
}): MainCommandConversationController {
  const { turns, client, guard, conversationKey, timeline, discussion, onLeaveDiscussion } = input;
  const [archives, setArchives] = useState<MainCommandChatArchive[]>([]);
  const [archivesLoaded, setArchivesLoaded] = useState(false);
  const [viewingArchiveId, setViewingArchiveId] = useState<string | null>(null);
  const [conversation, setConversation] = useState<MainCommandConversationState | null>(null);
  const { start: startChat, busy: startBusy, error: startError } = useStartMainCommandChat(client);
  const { continueFrom, busy: continueBusy, error: continueError } = useContinueMainCommandChat(client);
  const [freshContextResult, setFreshContextResult] = useState<FreshContextResult | null>(null);
  const mode = discussion ?? { kind: 'command' as const };
  const discussing = isDiscussionMode(mode);
  const orgId = conversationKey && conversationKey !== '' ? conversationKey : null;

  const refreshArchives = useCallback(async () => {
    if (orgId === null) {
      setArchives([]);
      setConversation(null);
      setArchivesLoaded(true);
      return;
    }
    const [rows, state] = await Promise.all([
      loadMainCommandChatArchives(client, orgId),
      loadMainCommandConversationState(client, orgId),
    ]);
    setArchives(rows);
    setConversation(state);
    setArchivesLoaded(true);
  }, [client, orgId]);

  useEffect(() => {
    setArchives([]);
    setArchivesLoaded(false);
    setViewingArchiveId(null);
    void refreshArchives();
  }, [refreshArchives]);

  const orderedArchives = useMemo(
    () => sortArchivesNewestFirst(withRolloverChatArchive(archives, conversation, turns, orgId)),
    [archives, conversation, turns, orgId],
  );
  const recentChats = useMemo(
    () => listRecentMainCommandChats({ current: conversation, archives: orderedArchives }),
    [conversation, orderedArchives],
  );
  const currentBoundary = latestArchive(orderedArchives);
  const viewingArchive = viewingArchiveId === null
    ? null
    : orderedArchives.find((item) => item.id === viewingArchiveId) ?? null;
  const viewingPrevious = viewingArchive === null
    ? null
    : previousArchive(orderedArchives, viewingArchive);

  const fallbackTimeline: SharedTimelineItem[] = useMemo(() => turns.map((turn, index) => ({
    id: `command:${turn.id}`,
    role: turn.mine ? 'owner' as const : 'overseer' as const,
    authority: 'command' as const,
    mine: turn.mine,
    text: turn.text,
    atLabel: turn.atLabel,
    sortKey: index,
    // #651: the time is its own element beside the byline, from sentAt.
    byline: turn.mine ? `You${turn.seq === null ? '' : ` · #${turn.seq}`}` : 'Brunel',
    statusLabel: turn.statusLabel,
    reason: turn.reason,
    offer: turn.offer,
    outcome: turn.outcome,
    threadId: null,
    commandTurn: turn,
    targetRoles: turn.mine ? ((turn.replyTargets ?? ['overseer']).filter((target): target is 'overseer' | 'ux' | 'core' =>
      target === 'overseer' || target === 'ux' || target === 'core')) : [],
    targetWorkerLabels: turn.mine ? (turn.workerTargets ?? []).map(() => 'Worker') : [],
    occurredAtMs: null,
    sentAt: turn.sentAt ?? null,
  })), [turns]);

  const sourceTimeline = timeline ?? fallbackTimeline;
  const visibleTimeline = useMemo(() => {
    if (viewingArchive !== null) return timelineInArchiveRange(sourceTimeline, viewingPrevious, viewingArchive);
    return timelineInArchiveRange(sourceTimeline, currentBoundary, null);
  }, [sourceTimeline, viewingArchive, viewingPrevious, currentBoundary]);

  const visibleTurns = useMemo(() => {
    if (viewingArchive !== null) {
      return commandTurnsInArchiveRange(
        turns,
        viewingPrevious?.throughCommandSeq ?? null,
        viewingArchive.throughCommandSeq,
      );
    }
    return commandTurnsInArchiveRange(turns, currentBoundary?.throughCommandSeq ?? null, null);
  }, [turns, viewingArchive, viewingPrevious, currentBoundary]);

  const digestTurnsFromVisible = (items: readonly CommandTurn[]): DigestTurn[] =>
    items.map((turn) => ({
      id: turn.id,
      mine: turn.mine,
      text: turn.text,
      seq: turn.seq,
      planId: turn.offer?.planId ?? null,
      resultId: null,
    }));

  const chatBusy = startBusy || continueBusy;
  const chatError = startError ?? continueError;

  const selectConversation = useCallback((archiveId: string | null) => {
    if (discussing) onLeaveDiscussion?.();
    setViewingArchiveId(archiveId);
  }, [discussing, onLeaveDiscussion]);

  const startNewChat = useCallback(() => {
    if (orgId === null || viewingArchive !== null || chatBusy) return;
    if (discussing) onLeaveDiscussion?.();
    const throughSeq = visibleTurns.length === 0
      ? null
      : archiveThroughCommandSeq(visibleTurns, currentBoundary);
    const digestTurns = digestTurnsFromVisible(visibleTurns);
    const title = conversation?.title?.trim() || titleFromFirstOwnerGoal(digestTurns);
    const digest = visibleTurns.length === 0
      ? null
      : redactDigestSecrets(buildConversationDigest({ turns: digestTurns, title }));
    void guard('post_owner_command', async () => {
      const saved = await startChat(orgId, throughSeq, {
        title: visibleTurns.length === 0 ? null : title,
        digest: digest as unknown as Record<string, unknown> | null,
      });
      if (saved === null) return;
      setViewingArchiveId(null);
      await refreshArchives();
    });
  }, [orgId, viewingArchive, chatBusy, discussing, onLeaveDiscussion, visibleTurns, currentBoundary, conversation, guard, startChat, refreshArchives]);

  const continueFromArchive = useCallback((archiveId: string) => {
    if (orgId === null || chatBusy) return;
    const throughSeq = viewingArchive === null && visibleTurns.length > 0
      ? archiveThroughCommandSeq(visibleTurns, currentBoundary)
      : null;
    void guard('post_owner_command', async () => {
      const saved = await continueFrom(orgId, archiveId, throughSeq);
      if (saved === null) return;
      setViewingArchiveId(null);
      await refreshArchives();
    });
  }, [orgId, chatBusy, viewingArchive, visibleTurns, currentBoundary, guard, continueFrom, refreshArchives]);

  const startFreshContext = useCallback(() => {
    if (orgId === null || viewingArchive !== null || chatBusy) return;
    setFreshContextResult(null);
    void guard('post_owner_command', async () => {
      setFreshContextResult(await startMainCommandFreshContext({ orgId, rpc: (name, args) => client.rpc(name, args) }));
      await refreshArchives();
    });
  }, [orgId, viewingArchive, chatBusy, guard, client, refreshArchives]);

  return {
    archivesLoaded,
    orderedArchives,
    conversation,
    recentChats,
    viewingArchiveId,
    viewingArchive,
    visibleTimeline,
    visibleTurns,
    chatBusy,
    chatError,
    orgId,
    selectConversation,
    startNewChat,
    continueFromArchive,
    startFreshContext,
    freshContextResult,
    continuedFromPreviousChat: continuedFromPreviousChat(viewingArchiveId, orderedArchives),
  };
}
